import type { GridV1EditorMode } from './grid-v1-toolbar';
import type { GridStackWidget } from 'gridstack';

import { Box } from '@chakra-ui/react';
import { GridStack } from 'gridstack';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import 'gridstack/dist/gridstack.min.css';
import './grid-v1-editor.scss';

import {
  defaultNewGridV1WidgetLayout,
  nextGridV1CellId,
} from './grid-v1-cells';
import { mergeGridV1LayoutWithMarkdown } from './grid-v1-serialize';
import {
  DEFAULT_GRID_V1_GRID_OPTIONS,
  type GridV1DocumentValue,
  type GridV1GridOptions,
  toLayoutWidgets,
} from './grid-v1-types';
import {
  MarkdownEditor,
  type GridCellMarkdownEditorProps,
} from './markdown-editor';

export interface GridV1EditorHandle {
  addCell: () => void;
}

interface GridV1EditorProps {
  mode: GridV1EditorMode;
  value: GridV1DocumentValue;
  onChange: (next: GridV1DocumentValue) => void;
  cellMarkdownEditorProps: GridCellMarkdownEditorProps;
}

function layoutWidgetsKey(widgets: GridV1DocumentValue['widgets']): string {
  return JSON.stringify(toLayoutWidgets(widgets));
}

function collectPortalTargets(container: HTMLElement): Map<string, Element> {
  const next = new Map<string, Element>();
  container.querySelectorAll('.grid-stack-item').forEach((item) => {
    const id =
      item.getAttribute('gs-id') ??
      item.getAttribute('id') ??
      item.getAttribute('data-gs-id');
    const content = item.querySelector('.grid-stack-item-content');
    if (id && content) {
      next.set(id, content);
    }
  });
  return next;
}

export const GridV1Editor = forwardRef<GridV1EditorHandle, GridV1EditorProps>(
  function GridV1Editor(
    { mode, value, onChange, cellMarkdownEditorProps },
    ref,
  ) {
    const containerRef = useRef<HTMLDivElement>(null);
    const gridRef = useRef<GridStack | null>(null);
    const cellMarkdownByIdRef = useRef<Map<string, string>>(new Map());
    const onChangeRef = useRef(onChange);
    const gridOptionsRef = useRef<GridV1GridOptions>(value.grid);
    const valueRef = useRef(value);
    const modeRef = useRef(mode);
    const layoutKeyRef = useRef('');
    const [portalTargets, setPortalTargets] = useState<Map<string, Element>>(
      () => new Map(),
    );

    const isViewMode = mode === 'view';

    onChangeRef.current = onChange;
    gridOptionsRef.current = value.grid;
    valueRef.current = value;
    modeRef.current = mode;

    const syncMarkdownMapFromValue = useCallback((doc: GridV1DocumentValue) => {
      const map = new Map<string, string>();
      for (const widget of doc.widgets) {
        map.set(widget.id, widget.markdown);
      }
      cellMarkdownByIdRef.current = map;
    }, []);

    const emitMergedDocument = useCallback(() => {
      const grid = gridRef.current;
      if (!grid || modeRef.current === 'view') return;

      const layout = grid.save(false) as Array<Record<string, unknown>>;
      const merged = mergeGridV1LayoutWithMarkdown(
        layout,
        cellMarkdownByIdRef.current,
        gridOptionsRef.current,
      );
      onChangeRef.current(merged);
    }, []);

    const refreshPortalTargets = useCallback(() => {
      const container = containerRef.current;
      if (!container) return;

      setPortalTargets(collectPortalTargets(container));
    }, []);

    const applyGridStaticMode = useCallback(
      (grid: GridStack, nextMode: GridV1EditorMode) => {
        grid.setStatic(nextMode === 'view');
      },
      [],
    );

    const addCell = useCallback(() => {
      const grid = gridRef.current;
      if (!grid || modeRef.current !== 'edit') return;

      const doc = valueRef.current;
      const id = nextGridV1CellId(doc.widgets);
      const columnCount =
        doc.grid.column ?? DEFAULT_GRID_V1_GRID_OPTIONS.column ?? 12;
      const { w, h } = defaultNewGridV1WidgetLayout(columnCount);
      grid.addWidget({ id, w, h });
    }, []);

    useImperativeHandle(ref, () => ({ addCell }), [addCell]);

    useEffect(() => {
      syncMarkdownMapFromValue(value);
    }, [syncMarkdownMapFromValue, value]);

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const opts = {
        ...DEFAULT_GRID_V1_GRID_OPTIONS,
        ...value.grid,
        float: true,
      };

      const grid = GridStack.init(opts, container);
      gridRef.current = grid;
      applyGridStaticMode(grid, modeRef.current);

      const onLayoutChange = () => {
        emitMergedDocument();
      };

      grid.on('change', onLayoutChange);
      grid.on('resizestop', onLayoutChange);
      grid.on('dragstop', onLayoutChange);

      grid.on('removed', (_event, items) => {
        for (const item of items) {
          const id = item.id;
          if (id) {
            cellMarkdownByIdRef.current.delete(id);
          }
        }
        refreshPortalTargets();
        emitMergedDocument();
      });

      grid.on('added', (_event, items) => {
        for (const item of items) {
          const id = item.id;
          if (id && !cellMarkdownByIdRef.current.has(id)) {
            cellMarkdownByIdRef.current.set(id, '');
          }
        }
        refreshPortalTargets();
        emitMergedDocument();
      });

      const layoutKey = layoutWidgetsKey(value.widgets);
      layoutKeyRef.current = layoutKey;
      grid.load(toLayoutWidgets(value.widgets) as GridStackWidget[]);
      requestAnimationFrame(() => {
        refreshPortalTargets();
      });

      return () => {
        grid.off('change');
        grid.off('resizestop');
        grid.off('dragstop');
        grid.off('removed');
        grid.off('added');
        grid.destroy(false);
        gridRef.current = null;
      };
      // Init once; external layout reload handled below.
      // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only GridStack init
    }, []);

    useEffect(() => {
      const grid = gridRef.current;
      if (!grid) return;

      applyGridStaticMode(grid, mode);
    }, [applyGridStaticMode, mode]);

    useEffect(() => {
      const grid = gridRef.current;
      if (!grid) return;

      const layoutKey = layoutWidgetsKey(value.widgets);
      if (layoutKey === layoutKeyRef.current) return;

      layoutKeyRef.current = layoutKey;
      syncMarkdownMapFromValue(value);
      grid.load(toLayoutWidgets(value.widgets) as GridStackWidget[]);
      requestAnimationFrame(() => {
        refreshPortalTargets();
      });
    }, [refreshPortalTargets, syncMarkdownMapFromValue, value]);

    const handleCellChange = useCallback(
      (cellId: string, markdown: string) => {
        if (modeRef.current === 'view') return;

        cellMarkdownByIdRef.current.set(cellId, markdown);
        emitMergedDocument();
      },
      [emitMergedDocument],
    );

    const markdownForCell = useCallback(
      (cellId: string) =>
        value.widgets.find((w) => w.id === cellId)?.markdown ??
        cellMarkdownByIdRef.current.get(cellId) ??
        '',
      [value.widgets],
    );

    return (
      <Box
        className={
          isViewMode ? 'grid-v1-editor grid-v1-editor--view' : 'grid-v1-editor'
        }
        flex={1}
        minH={0}
        display="flex"
        flexDirection="column"
      >
        <Box
          ref={containerRef}
          className="grid-stack grid-v1-editor-stack"
          flex={1}
        />
        {Array.from(portalTargets.entries()).map(([cellId, target]) =>
          createPortal(
            <MarkdownEditor
              key={cellId}
              {...cellMarkdownEditorProps}
              variant="grid-cell"
              editable={!isViewMode}
              value={markdownForCell(cellId)}
              onChange={(md) => handleCellChange(cellId, md)}
            />,
            target,
            cellId,
          ),
        )}
      </Box>
    );
  },
);
